// === PERSONENAUTO SCREEN ===
// Personenauto (EDP) insurance product screen
// Address search + placeholder for vehicle-specific fields

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Car, User, ShieldCheck } from 'phosphor-react';
import {
  useWorkflow,
  useSharedAddress,
  useConfig,
  WorkflowPageLayout,
  bffClient,
  formatPrice,
  DatePicker,
  DateOfBirthField,
  isDebugMode,
} from '@sluitstraat/core';
import { getApplicationName } from '../../utils/appName';
import { formatInitials } from '../../utils/formatInitials';
import { personenAutoStyles as styles } from '../../styles/commonStyles';
import { INFOFOLIO_SESSION_KEYS, shouldHideAddressSection } from '../../utils/addressSharing';
import { applyAutoGarantieFlags } from '../../utils/autoGarantie';
import { DebugPanel } from '../../components/DebugPanel';
import { WithHelpSidebar } from '../../components/HelpCard';
import { CoverageOptionCard } from '../../components/CoverageOptionCard';
import { InfoTooltip } from '../../components/InfoTooltip';
import { WorkflowNavButtons } from '../../components/WorkflowNavButtons';
import { useWorkflowNav } from '../../hooks/useWorkflowNav';
import AddressSection, { Address } from '../../components/AddressSection';
import { SelectField } from '../../components/SelectField';
import { TextField } from '../../components/TextField';
import { SectionHeader } from '../../components/SectionHeader';
import { formatLicensePlate } from '../../utils/licensePlate';
import { setIn } from '../../utils/setIn';
import { stableStringify } from '../../utils/stableStringify';
import {
  AUTO_CODELIST,
  SECURITY_CLASS_CODES,
  afmkmCode,
  AUTO_VARIANTS,
  AUTO_OPTIONAL_COVERAGES,
  hasCascoSelected,
  buildAutoPayload,
  mergeAutoResult,
  validatePersonenauto,
  computeKwWarning,
  scrollToFirstError,
  useUpstreamSync,
  VehicleSummaryCard,
  CoverageMatrix,
  CoverageHighlights,
  ValidationSummary,
  calculateWithRetry,
  useCoverageCalculation,
  useKentekenLookup,
} from './personenauto';

const SCREEN_ID = 'personenauto';

// NOTE: PP_BMNAAND - B/M trede = PP_SCHDVRY (schadevrije jaren) + PP_BMINSCH (inschalingstrede).



// Kenteken formatting lives in utils/licensePlate so the summary card formats it identically.

// BA fields derived from the Infofolio property lookup. When PA's address changes we clear these
// on every downstream product so their mount effect re-fetches fresh data for the new address
// (otherwise Woonhuis/Inboedel would fire Infofolio for the old postcode on the next navigation).
const INFOFOLIO_DERIVED_BA_FIELDS = [
  'BA_BOUWJR', 'BA_HERBWRD', 'BA_OPPWON', 'BA_TYPEWON',
  'BA_AARDBW', 'BA_AARDDAK', 'BA_MONUMEN', 'BA_WONING', 'BA_EIGENWO',
  'BA_DAKCONS', 'BA_DAKBED', 'BA_DAKBED_CODE', 'BA_BGM2', 'BA_TOPPBGB',
  'BA_HERBVST', 'BA_RIETDAK', 'BA_SRTRDAK', 'BA_ZONNPAN', 'BA_PERMBEW',
  'BA_OPZPKVA',
];

export const PersonenAutoScreen: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { getStepData, saveStepData, selectedProducts, relationData } = useWorkflow();
  const { config } = useConfig();
  // Schadeverzekering Inzittenden (SV_VERZSOM) - fixed insured sum, no user choice.
  // Matches the auto.json product schema (SV_VERZSOM default = 1000000). Config can still
  // override the default, but there is no dropdown of alternative amounts.
  const passengerInsuredSumConfig = (config as any)?.passengerInsuredSum;
  const passengerInsuredSumDefault = Number(passengerInsuredSumConfig?.default ?? 1000000);

  // Screen data state
  const [data, setData] = useState<any>(() => {
    const saved = getStepData(SCREEN_ID);
    if (!saved) return { Contractdocument: { PP: { OB: {}, VP: {}, BA: {}, PP_BRANCHE: '021' } } };
    // Previously-saved sessions may hold OB_AFMKM in another form (zero-stripped code from
    // old BFF echoes, or band kilometers from the wire format); normalize to the dropdown
    // code on mount instead of falling back to "00010". The EXPERIMENT(OB_AFMKMCD) echo is
    // preferred when present — see personenauto/fields.ts; km reverse-mapping covers its removal.
    const ob = saved?.Contractdocument?.PP?.OB;
    if (ob?.OB_AFMKM !== undefined) {
      ob.OB_AFMKM = afmkmCode(ob.OB_AFMKMCD ?? ob.OB_AFMKM) ?? String(ob.OB_AFMKM).padStart(5, '0');
      delete ob.OB_AFMKMCD;
    }
    return saved;
  });

  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  // Debug & calculation states
  const [showCoverageMatrix, setShowCoverageMatrix] = useState(false);

  // UI-only ex-provision display price for a single coverage section. Mirrors
  // `computePersonenautoDisplayPremium` in core, but per-section so the individual "+€X"
  // badges on this screen arithmetically add to the total coverage badge.
  // No discount is configured for Personenauto today, but the hook is here so a future
  // `config.personenautoDiscount.applyToData === false` can overlay the discount at display.
  const sectionDisplayPrice = (btp: number | string | undefined): number => {
    const n = Number(btp) || 0;
    if (n <= 0) return 0;
    const disc = (config as any)?.personenautoDiscount;
    const discActive = disc?.enabled && (disc.percentage ?? 0) > 0 && disc?.applyToData === false;
    const factor = discActive ? 1 - disc.percentage / 100 : 1;
    const pprc = 20;
    return Math.floor(n * factor * (1 - pprc / 100) * 1.21 * 100) / 100;
  };

  const resolveCoverageEntry = (
    pp: any,
    coverage: { section: string; cgIndex?: number; cgCodes?: string[] },
  ): any => {
    const sectionData = pp?.[coverage.section];
    if (coverage.cgCodes && Array.isArray(sectionData)) {
      return sectionData.find((e: any) => coverage.cgCodes!.includes(String(e?.CG_CODE)));
    }
    if (coverage.cgIndex !== undefined) {
      return Array.isArray(sectionData) ? sectionData[coverage.cgIndex] : undefined;
    }
    return Array.isArray(sectionData) ? sectionData[0] : sectionData;
  };

  // Shared address hook
  const { address: sharedAddress } = useSharedAddress();

  // Refs for deduplication
  // Track last address for which Infofolio was fetched (to avoid duplicate calls)
  const lastInfofolioAddress = useRef<string>('');

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
    sessionStorage.setItem('workflow_current_page', SCREEN_ID);
  }, []);

  // Validate required data on mount
  useEffect(() => {
    if (!selectedProducts?.personenauto) {
      navigate('/workflow/product-selection', { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only guard; re-running on selectedProducts/navigate identity would redirect mid-flow
  }, []);

  // Seed the policyholder address (BA/VP) from the shared slice / upstream products.
  useUpstreamSync({ data, sharedAddress, selectedProducts, getStepData, setData, saveStepData, screenId: SCREEN_ID });

  /**
   * Fetch Infofolio property info and cache key fields in sessionStorage.
   * Personenauto doesn't display these fields itself, but Woonhuis/Inboedel
   * read the cached values when the user navigates there later.
   */
  const fetchPropertyInfoForCache = useCallback(async (postalCode: string, houseNumber: string, addition?: string) => {
    const key = `${postalCode}|${houseNumber}|${addition || ''}`;
    if (lastInfofolioAddress.current === key) return;
    lastInfofolioAddress.current = key;

    try {
      const response = await bffClient.getPropertyInfo(postalCode, houseNumber, addition || undefined);
      const propertyData = response?.BA;
      if (!propertyData) return;

      // Cache herbouwwaarde so Woonhuis can pick it up via sessionStorage
      // (matches the existing pattern in WoonhuisScreen / InboedelScreen)
      if (propertyData.BA_HERBWRD) {
        sessionStorage.setItem('infofolio_BA_HERBWRD', String(Number(propertyData.BA_HERBWRD)));
      }
    } catch (error) {
      // Silently fail - Infofolio data is an optional enhancement
    }
  }, []);

  /**
   * Push PA's new address into every downstream product's saved step data so their mount
   * effect sees the new address on re-entry. Clears BA_STRAAT/BA_PLAATS and all Infofolio-derived
   * BA fields so Woonhuis/Inboedel's mount-effect triggers a fresh autocomplete + fetchPropertyInfo
   * for the new postcode (instead of re-firing Infofolio for the stale one).
   * VP is always synced (policyholder follows PA); BA is only overwritten when the downstream
   * product has RisicoAdresGelijk='J' (risk address == policyholder), which is the default.
   */
  const syncAddressToDownstreamProducts = useCallback(
    (newAddress: { BA_PCODE: string; BA_HUISNR: string | number; BA_TOEVOEG: string; BA_STRAAT: string; BA_PLAATS: string }, vpHuisnr: number) => {
      const downstream: Array<'woonhuis' | 'inboedel' | 'aansprakelijkheid' | 'rechtsbijstand'> =
        ['woonhuis', 'inboedel', 'aansprakelijkheid', 'rechtsbijstand'];
      let anyChanged = false;
      for (const product of downstream) {
        if (!selectedProducts?.[product]) continue;
        const stepData = getStepData(product);
        if (!stepData?.Contractdocument?.PP) continue;

        const productBA = stepData.Contractdocument.PP.BA || {};
        // Skip products whose address is already in sync with PA
        if (
          productBA.BA_PCODE === newAddress.BA_PCODE &&
          String(productBA.BA_HUISNR) === String(newAddress.BA_HUISNR) &&
          (productBA.BA_TOEVOEG || '') === (newAddress.BA_TOEVOEG || '')
        ) {
          continue;
        }
        anyChanged = true;

        const risicoGelijk = stepData.Supportsection?.Divers?.RisicoAdresGelijk ?? 'J';
        // For AVP/Rechtsbijstand, BA mirrors the policyholder address. Woonhuis/Inboedel
        // have an explicit risicoAdresGelijk toggle — only sync BA when the user kept them linked.
        const shouldSyncBA = product === 'aansprakelijkheid' || product === 'rechtsbijstand' || risicoGelijk === 'J';

        const cleanedBA: any = { ...productBA };
        for (const field of INFOFOLIO_DERIVED_BA_FIELDS) delete cleanedBA[field];

        // Inboedel gates its fetchPropertyInfo on (BA_TYPEWON || BA_HERBWRD) && IP_VERZSOM.
        // Once linkWoonhuisAddress backfills BA_TYPEWON from a freshly-fetched Woonhuis, the
        // only way to force a refetch is to clear the Inboedel-specific inboedelwaarde fields.
        const nextIP = product === 'inboedel' && stepData.Contractdocument.PP.IP
          ? (() => {
              const ip = { ...stepData.Contractdocument.PP.IP };
              delete ip.IP_VERZSOM;
              return ip;
            })()
          : undefined;
        const nextIB = product === 'inboedel' && stepData.Contractdocument.PP.IB
          ? (() => {
              const ib = { ...stepData.Contractdocument.PP.IB };
              delete ib.IB_INBVST;
              return ib;
            })()
          : undefined;

        const updatedDownstream = {
          ...stepData,
          Contractdocument: {
            ...stepData.Contractdocument,
            PP: {
              ...stepData.Contractdocument.PP,
              ...(nextIP ? { IP: nextIP } : {}),
              ...(nextIB ? { IB: nextIB } : {}),
              VP: {
                ...(stepData.Contractdocument.PP.VP || {}),
                VP_PCODE: newAddress.BA_PCODE,
                VP_HUISNR: vpHuisnr,
                VP_TOEVOEG: newAddress.BA_TOEVOEG || '',
                VP_STRAAT: newAddress.BA_STRAAT,
                VP_PLAATS: newAddress.BA_PLAATS,
              },
              ...(shouldSyncBA
                ? {
                    BA: (() => {
                      const nextBA = {
                        ...cleanedBA,
                        BA_PCODE: newAddress.BA_PCODE,
                        BA_HUISNR: newAddress.BA_HUISNR,
                        BA_TOEVOEG: newAddress.BA_TOEVOEG || '',
                      };
                      // Strip the looked-up street/city so the downstream mount effect
                      // (e.g. Woonhuis line 2740) triggers a fresh lookup + fetchPropertyInfo.
                      delete nextBA.BA_STRAAT;
                      delete nextBA.BA_PLAATS;
                      return nextBA;
                    })(),
                  }
                : {}),
            },
          },
        };
        saveStepData(product, updatedDownstream);
      }
      // Clear the Infofolio sessionStorage caches so downstream screens don't read stale values
      // between mount and their own fetchPropertyInfo call finishing.
      if (anyChanged) {
        for (const key of INFOFOLIO_SESSION_KEYS) sessionStorage.removeItem(key);
      }
    },
    [selectedProducts, getStepData, saveStepData],
  );

  // Policyholder address (BA) edited via the shared AddressSection. Write BA + VP, clear
  // validation, and — once a full address resolves — push it downstream and refresh the
  // Infofolio cache (the PA-specific glue AddressSection doesn't own). The shared-slice sync
  // is handled by AddressSection itself (shareAddress default = true).
  const handleBaAddressChange = useCallback((a: Address) => {
    const huisNr = Number(a.houseNumber);
    const vpHuisnr = isNaN(huisNr) ? 0 : huisNr;
    setData((prev: any) => {
      const updated = {
        ...prev,
        Contractdocument: {
          ...prev?.Contractdocument,
          PP: {
            ...prev?.Contractdocument?.PP,
            BA: {
              ...prev?.Contractdocument?.PP?.BA,
              BA_PCODE: a.postcode,
              BA_HUISNR: a.houseNumber,
              BA_TOEVOEG: a.addition || '',
              BA_STRAAT: a.street || '',
              BA_PLAATS: a.city || '',
            },
            VP: {
              ...prev?.Contractdocument?.PP?.VP,
              VP_PCODE: a.postcode,
              VP_HUISNR: vpHuisnr,
              VP_TOEVOEG: a.addition || '',
              VP_STRAAT: a.street || '',
              VP_PLAATS: a.city || '',
            },
          },
        },
      };
      setTimeout(() => saveStepData(SCREEN_ID, updated), 0);
      return updated;
    });

    setValidationErrors(prev => {
      const u = { ...prev };
      delete u['BA_PCODE'];
      delete u['BA_HUISNR'];
      delete u['_addressValidation'];
      return u;
    });

    if (a.street && a.city) {
      const newAddress = {
        BA_PCODE: a.postcode,
        BA_HUISNR: a.houseNumber,
        BA_TOEVOEG: a.addition || '',
        BA_STRAAT: a.street,
        BA_PLAATS: a.city,
      };
      syncAddressToDownstreamProducts(newAddress, vpHuisnr);
      fetchPropertyInfoForCache(a.postcode, a.houseNumber, a.addition || undefined);
    }
  }, [saveStepData, syncAddressToDownstreamProducts, fetchPropertyInfoForCache]);

  // Kenteken (license plate) autocomplete lookup
  const {
    kentekenInputValue,
    setKentekenInputValue,
    isLookingUpKenteken,
    kentekenLookupError,
    kentekenDataFilled,
  } = useKentekenLookup({ data, setData, saveStepData, screenId: SCREEN_ID, t });

  // Build a clean calculation payload for a given Hoofddekking variant
  // Build a clean calculation payload for a Hoofddekking variant. Thin wrapper over the pure
  // buildAutoPayload (calc.ts) — injects the impure now / baseUrl / appName plus closure data.
  const buildCalculationPayload = useCallback(async (baseData: any, hoofddekking: 'WA' | 'WA+' | 'WA+volledig') => {
    const appName = await getApplicationName();
    return buildAutoPayload(baseData, hoofddekking, {
      now: new Date(),
      baseUrl: window.location.origin + '/',
      appName,
      relationData,
      passengerInsuredSumDefault,
    });
  }, [relationData, passengerInsuredSumDefault]);

  // Merge BFF result into data state for the currently selected variant
  const mergeResultIntoData = useCallback(
    (bffOutput: any, prevData: any) => mergeAutoResult(bffOutput, prevData),
    [],
  );

  const getValidationErrors = useCallback((): Record<string, string> => validatePersonenauto(data), [data]);

  // Reactive kW vs driver age warning - shows as soon as kenteken data is available
  const kwWarning = useMemo((): string | null => {
    const key = computeKwWarning(data, relationData);
    return key ? t(key) : null;
  // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the exact inputs computeKwWarning reads; data/relationData as a whole would recompute per keystroke
  }, [t, data?.Contractdocument?.PP?.OB?.OB_AANTKW, data?.Contractdocument?.PP?.OB?.OB_MERK, data?.Contractdocument?.PP?.BS?.BS_GEBDAT, data?.Supportsection?.Divers?.Hoofdbestuurder, relationData?.clientInfo?.dateOfBirth]);

  const validateForm = useCallback((): { isValid: boolean; errors: Record<string, string> } => {
    const errors = getValidationErrors();
    if (kwWarning) errors['_kwValidation'] = kwWarning;
    setValidationErrors(errors);
    if (Object.keys(errors).length > 0) {
      return { isValid: false, errors };
    }
    return { isValid: true, errors: {} };
  }, [getValidationErrors, kwWarning]);

  // Re-validate while the user is filling in fields, so required-field
  // errors clear as soon as the missing value is provided. Only runs once
  // the user has triggered validation at least once. BA_PCODE / BA_HUISNR
  // are intentionally excluded — their format validation is managed by the
  // inline onBlur handlers and must not be clobbered here.
  useEffect(() => {
    setValidationErrors(prev => {
      if (Object.keys(prev).length === 0) return prev;
      const fresh = getValidationErrors();
      if (kwWarning) fresh['_kwValidation'] = kwWarning;
      const clearable = new Set([
        '_addressValidation', 'OB_KENTEKE', 'OB_AFMKM', 'PP_SCHDVRY',
        'OB_AANKDAT', '_eersteEigenaar', 'BS_RELSRTC', 'BS_VOORL', 'BS_ANAAM',
        'BS_GESLACH', 'BS_GEBDAT', '_kwValidation',
      ]);
      const next: Record<string, string> = { ...prev };
      let changed = false;
      for (const key of Object.keys(prev)) {
        if (clearable.has(key) && !fresh[key]) {
          delete next[key];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [data, kwWarning, getValidationErrors]);

  const {
    isCalculating,
    calculationError,
    setCalculationError,
    premiumsByVariant,
    isVariantRefused,
    cascoRefused,
    handleCalculateAll,
    recalculateSingleVariant,
    lastCalculationInput,
    lastCalculationOutput,
    calcInFlightRef,
    restampVariantCache,
  } = useCoverageCalculation({
    data, setData, buildCalculationPayload, mergeResultIntoData,
    saveStepData, screenId: SCREEN_ID, kwWarning, t,
  });

  const handleManualCalculate = useCallback(() => {
    const { isValid, errors } = validateForm();
    if (!isValid) {
      setCalculationError(t('screens.personenauto.validation.fillAllRequired'));
      scrollToFirstError(errors);
      return;
    }
    handleCalculateAll();
  }, [validateForm, handleCalculateAll, setCalculationError, t]);

  // Localhost-only debug helper: overwrite a single OB_* vehicle field and persist.
  const handleDebugObChange = useCallback((key: string, raw: string, isNumber: boolean) => {
    const value = isNumber ? (raw === '' ? '' : Number(raw)) : raw;
    setData((prev: any) => {
      const updated = {
        ...prev,
        Contractdocument: {
          ...prev.Contractdocument,
          PP: { ...prev.Contractdocument?.PP, OB: { ...prev.Contractdocument?.PP?.OB, [key]: value } },
        },
      };
      setTimeout(() => saveStepData(SCREEN_ID, updated), 0);
      return updated;
    });
  }, [setData, saveStepData]);

  const { currentStep, totalSteps, productIndex, totalProducts, goToNext, goToPrevious } = useWorkflowNav(SCREEN_ID);

  const [isNextCalculating, setIsNextCalculating] = useState(false);

  const handleNext = async () => {
    const { isValid, errors } = validateForm();
    if (!isValid) {
      setCalculationError(t('screens.personenauto.validation.fillAllRequired'));
      scrollToFirstError(errors);
      return;
    }
    setCalculationError(null);

    const dekkingen = data?.Supportsection?.Dekkingen || {};
    const hoofddekking = (dekkingen.Hoofddekking || 'WA') as 'WA' | 'WA+' | 'WA+volledig';

    if (calcInFlightRef.current) return;
    calcInFlightRef.current = true;
    try {
      setIsNextCalculating(true);

      // CG: filter per-entry by toggle. The intermediate calc always carries every eligible
      // entry (so the BFF prices VKH / NW / AKW upfront for the in-page display), but the
      // FINAL request should only carry the ones the advisor actually opted into — otherwise
      // their premiums end up in PP_TTOT. After the filter we re-apply the garantie flags +
      // maanden (realignCaFlags below) so the surviving CG codes drive 12 vs 36 maanden.
      const isWaOnly = !hasCascoSelected(dekkingen);
      const keepCgEntry = (entry: any): boolean => {
        const code = String(entry?.CG_CODE);
        if (code === '2051') return dekkingen.Vrije_keuze_herstelbedrijf === 'J';
        if (code === '2040') return dekkingen.Nieuwwaarderegeling === 'J';
        if (code === '2041') return dekkingen.Aanschafwaarderegeling === 'J';
        return false;
      };
      // After the CG filter, re-derive garantie flags + maanden: the flag follows
      // eligibility (stays 'J' even when the advisor skipped the upgrade), the maanden
      // drop back to 12 once the matching CG code is gone.
      const realignCaFlags = (target: any) => {
        applyAutoGarantieFlags(target?.Contractdocument?.PP);
      };

      const buildFinalPayload = async (baseData: any) => {
        const p = await buildCalculationPayload(baseData, hoofddekking);
        if (dekkingen.Rechtsbijstand !== 'J') delete p.input.Contractdocument.PP.RP;
        if (dekkingen.Schade_Inzittenden !== 'J') delete p.input.Contractdocument.PP.SV;
        if (dekkingen.Pechhulp !== 'J') delete p.input.Contractdocument.PP.PH;
        if (isWaOnly) {
          p.input.Contractdocument.PP.CG = [];
        } else {
          const cg: any[] = Array.isArray(p.input.Contractdocument.PP.CG) ? p.input.Contractdocument.PP.CG : [];
          p.input.Contractdocument.PP.CG = cg.filter(keepCgEntry);
        }
        realignCaFlags(p.input);
        return p;
      };

      const payload = await buildFinalPayload(data);

      if (stableStringify(payload) === sessionStorage.getItem('personenauto_final_payload')) {
        goToNext();
        return;
      }

      // Final calculation with only selected coverages
      const bffResult = await calculateWithRetry(payload);

      if (bffResult?.output) {
        const mergedData = mergeResultIntoData(bffResult.output, data);
        mergedData.completed = true;

        // Ensure OB is preserved
        mergedData.Contractdocument.PP.OB = data.Contractdocument?.PP?.OB || {};

        // Remove unselected optional coverages from the final saved data
        if (dekkingen.Rechtsbijstand !== 'J') delete mergedData.Contractdocument.PP.RP;
        if (dekkingen.Schade_Inzittenden !== 'J') delete mergedData.Contractdocument.PP.SV;
        if (dekkingen.Pechhulp !== 'J') delete mergedData.Contractdocument.PP.PH;
        if (isWaOnly) {
          mergedData.Contractdocument.PP.CG = [];
        } else {
          const cg: any[] = Array.isArray(mergedData.Contractdocument.PP.CG) ? mergedData.Contractdocument.PP.CG : [];
          mergedData.Contractdocument.PP.CG = cg.filter(keepCgEntry);
        }
        realignCaFlags(mergedData);

        saveStepData(SCREEN_ID, mergedData);
        sessionStorage.setItem('personenauto_final_payload', stableStringify(await buildFinalPayload(mergedData)));
        restampVariantCache(mergedData);
      } else {
        // Fallback: save current data if BFF returned no output
        const fallbackData = { ...data, completed: true };
        saveStepData(SCREEN_ID, fallbackData);
      }

      goToNext();
    } catch (error: any) {
      console.error('[PersonenAuto] Final calculation error:', error);
      setCalculationError(error?.message || t('common.premiumCalculationError'));
    } finally {
      calcInFlightRef.current = false;
      setIsNextCalculating(false);
    }
  };

  const handleBack = () => {
    saveStepData(SCREEN_ID, data);
    goToPrevious();
  };

  return (
    <WorkflowPageLayout currentStep={currentStep} totalSteps={totalSteps}>
      <main style={styles.page}>
        <div style={styles.container}>
          <WithHelpSidebar stackEarly>
            <header style={styles.contentHeader}>
              <h1 style={styles.title}>{t('products.personenauto.name')}</h1>
            </header>

            <div style={styles.formColumn}>

          {/* Policyholder address — its own card (AddressSection renders the "Adresgegevens"
              header itself). Hidden when an upstream selected product already supplied the address.
              Personenauto follows the Wonen & Gezin products in the workflow, so with any of those
              selected the address arrives via the shared slice (useUpstreamSync seeds BA/VP) and
              this block stays hidden; it only shows when Personenauto is the first selected product. */}
          {!shouldHideAddressSection('personenauto', selectedProducts, getStepData) && (
            <AddressSection
              icon={User}
              value={{
                postcode: data?.Contractdocument?.PP?.BA?.BA_PCODE || '',
                houseNumber: data?.Contractdocument?.PP?.BA?.BA_HUISNR != null ? String(data.Contractdocument.PP.BA.BA_HUISNR) : '',
                addition: data?.Contractdocument?.PP?.BA?.BA_TOEVOEG || '',
                street: data?.Contractdocument?.PP?.BA?.BA_STRAAT || '',
                city: data?.Contractdocument?.PP?.BA?.BA_PLAATS || '',
              }}
              onChange={handleBaAddressChange}
              showErrors={!!validationErrors['BA_PCODE'] || !!validationErrors['_addressValidation']}
            />
          )}

          {/* Driver Section */}
          <div style={styles.addressContainer}>
            <SectionHeader icon={User} title={t('screens.personenauto.sections.driver')} />

            {/* Driver fields */}
            <div className="personenauto-field-pair" style={styles.rowGap16}>
              {/* Jaarkilometrage */}
              <SelectField
                id="afmkm"
                label={t('screens.personenauto.fields.annualMileage')}
                required
                containerStyle={styles.flex1}
                value={data?.Contractdocument?.PP?.OB?.OB_AFMKM ?? ''}
                onChange={(v) => setData((prev: any) => setIn(prev, ['Contractdocument', 'PP', 'OB', 'OB_AFMKM'], v === '' ? undefined : v))}
                placeholder={t('screens.personenauto.placeholders.select')}
                error={validationErrors['OB_AFMKM'] ? t(validationErrors['OB_AFMKM']) : null}
                options={[
                  { value: '00010', label: t('screens.personenauto.mileageOptions.upTo7500') },
                  { value: '00007', label: t('screens.personenauto.mileageOptions.upTo10000') },
                  { value: '00008', label: t('screens.personenauto.mileageOptions.upTo15000') },
                  { value: '00002', label: t('screens.personenauto.mileageOptions.upTo20000') },
                  { value: '00003', label: t('screens.personenauto.mileageOptions.unlimited') },
                ]}
              />

              {/* Schadevrije jaren */}
              <div style={{ ...styles.fieldContainer, ...styles.flex1 }}>
                <label className="field-label--required" style={styles.fieldLabel} htmlFor="schdvry">
                  {t('screens.personenauto.fields.noClaimsYears')}
                </label>
                <input
                  id="schdvry"
                  type="number"
                  inputMode="numeric"
                  min="0"
                  step="1"
                  value={data?.Contractdocument?.PP?.PP_SCHDVRY ?? ''}
                  onChange={(e) => {
                    const raw = e.target.value.replace(/[^0-9]/g, '');
                    const val = raw === '' ? undefined : parseInt(raw, 10);
                    setData((prev: any) => ({
                      ...prev,
                      Contractdocument: {
                        ...prev.Contractdocument,
                        PP: {
                          ...prev.Contractdocument?.PP,
                          PP_SCHDVRY: val,
                          PP_BMNAAND: (val || 0) + (Number(prev.Contractdocument?.PP?.PP_BMINSCH) || AUTO_CODELIST.PP_BMINSCH),
                        },
                      },
                    }));
                  }}
                  onKeyDown={(e) => {
                    if (['e', 'E', '+', '-', '.', ','].includes(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  style={{ ...styles.fieldInput, ...(validationErrors['PP_SCHDVRY'] ? styles.fieldInputError : {}) }}
                  placeholder={t('screens.personenauto.placeholders.noClaimsYears')}
                />
                {validationErrors['PP_SCHDVRY'] && <p style={styles.errorMessage}>{t(validationErrors['PP_SCHDVRY'])}</p>}
              </div>
            </div>

            {/* Hoofdbestuurder */}
            <div style={styles.fieldContainer}>
              <label className="field-label--required" style={styles.fieldLabel}>
                {t('screens.personenauto.fields.mainDriver')}
              </label>
              <div style={styles.radioGroup}>
                {(['J', 'N'] as const).map((optValue) => {
                  const selected = (data?.Supportsection?.Divers?.Hoofdbestuurder || 'J') === optValue;
                  return (
                    <div
                      key={optValue}
                      style={selected ? styles.radioOptionSelected : styles.radioOption}
                      onClick={() => {
                        setData((prev: any) => {
                          const updated = {
                            ...prev,
                            Supportsection: {
                              ...prev.Supportsection,
                              Divers: { ...prev.Supportsection?.Divers, Hoofdbestuurder: optValue },
                            },
                          };
                          if (optValue === 'J') {
                            // Driver IS policyholder: overwrite BS with client info
                            const ci = relationData?.clientInfo;
                            const vpData = prev.Contractdocument?.PP?.VP || {};
                            let gebdat = undefined as number | undefined;
                            if (ci?.dateOfBirth) {
                              let ds = ci.dateOfBirth.replace(/-/g, '');
                              if (ds.length === 8) ds = ds.substring(4, 8) + ds.substring(2, 4) + ds.substring(0, 2);
                              const num = Number(ds);
                              if (!isNaN(num) && ds.length === 8) gebdat = num;
                            }
                            updated.Contractdocument = {
                              ...prev.Contractdocument,
                              PP: {
                                ...prev.Contractdocument?.PP,
                                BS: {
                                  ...prev.Contractdocument?.PP?.BS,
                                  BS_RELSRTC: 'A',
                                  BS_ANAAM: ci?.lastName || '',
                                  BS_VOORL: ci?.initials || '',
                                  BS_VOORV: '',
                                  BS_GESLACH: ci?.gender || 'O',
                                  ...(gebdat !== undefined ? { BS_GEBDAT: gebdat } : {}),
                                  BS_HUISNR: vpData.VP_HUISNR,
                                  BS_PCODE: vpData.VP_PCODE,
                                  BS_PLAATS: vpData.VP_PLAATS,
                                  BS_STRAAT: vpData.VP_STRAAT,
                                  BS_TOEVOEG: vpData.VP_TOEVOEG,
                                },
                              },
                            };
                          }
                          // When switching to 'N', keep existing BS data untouched (user-entered values persist)
                          return updated;
                        });
                      }}
                    >
                      <div style={selected ? styles.radioDotSelected : styles.radioDot}>
                        {selected && <div style={styles.radioDotInner} />}
                      </div>
                      {optValue === 'J' ? t('common.yes') : t('common.no')}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* BS fields - when driver is different from policyholder */}
            {(data?.Supportsection?.Divers?.Hoofdbestuurder ?? 'J') === 'N' && (
              <>
                <hr style={styles.sectionDivider} />
                <SelectField
                  id="bs-relsrtc"
                  label={t('screens.personenauto.fields.driverRelation', 'Relatie tot verzekeringnemer')}
                  required
                  value={data?.Contractdocument?.PP?.BS?.BS_RELSRTC || ''}
                  onChange={(v) => setData((prev: any) => setIn(prev, ['Contractdocument', 'PP', 'BS', 'BS_RELSRTC'], v))}
                  placeholder={t('common.select', 'Selecteer...')}
                  error={validationErrors['BS_RELSRTC'] ? t(validationErrors['BS_RELSRTC']) : null}
                  options={[
                    { value: 'T', label: t('screens.personenauto.fields.driverRelationPartner', 'Partner') },
                    { value: 'C', label: t('screens.personenauto.fields.driverRelationChild', 'Kind(eren)') },
                  ]}
                />

                <div className="client-info-name-layout">
                  <TextField
                    id="bs-voorl"
                    label={t('form.initials', 'Voorletters')}
                    required
                    value={data?.Contractdocument?.PP?.BS?.BS_VOORL || ''}
                    onChange={(v) => setData((prev: any) => setIn(prev, ['Contractdocument', 'PP', 'BS', 'BS_VOORL'], v))}
                    onBlur={() => {
                      const formatted = formatInitials(data?.Contractdocument?.PP?.BS?.BS_VOORL || '');
                      if (formatted !== data?.Contractdocument?.PP?.BS?.BS_VOORL) {
                        setData((prev: any) => setIn(prev, ['Contractdocument', 'PP', 'BS', 'BS_VOORL'], formatted));
                      }
                    }}
                    placeholder="J.A."
                    maxLength={20}
                    error={validationErrors['BS_VOORL'] ? t(validationErrors['BS_VOORL']) : null}
                  />
                  <TextField
                    label={t('form.affix', 'Tussenvoegsel')}
                    value={data?.Contractdocument?.PP?.BS?.BS_VOORV || ''}
                    onChange={(v) => setData((prev: any) => setIn(prev, ['Contractdocument', 'PP', 'BS', 'BS_VOORV'], v))}
                    placeholder="van der"
                  />
                  <TextField
                    id="bs-anaam"
                    label={t('form.lastName', 'Achternaam')}
                    required
                    value={data?.Contractdocument?.PP?.BS?.BS_ANAAM || ''}
                    onChange={(v) => setData((prev: any) => setIn(prev, ['Contractdocument', 'PP', 'BS', 'BS_ANAAM'], v))}
                    placeholder="Jansen"
                    error={validationErrors['BS_ANAAM'] ? t(validationErrors['BS_ANAAM']) : null}
                  />
                </div>

                <div style={styles.rowGap16}>
                  <SelectField
                    id="bs-gender"
                    label={t('form.gender', 'Geslacht')}
                    required
                    containerStyle={styles.flex1}
                    value={data?.Contractdocument?.PP?.BS?.BS_GESLACH || ''}
                    onChange={(v) => setData((prev: any) => setIn(prev, ['Contractdocument', 'PP', 'BS', 'BS_GESLACH'], v))}
                    placeholder={t('form.genderPlaceholder', 'Selecteer...')}
                    error={validationErrors['BS_GESLACH'] ? t(validationErrors['BS_GESLACH']) : null}
                    options={[
                      { value: 'M', label: t('form.genderOptions.male', 'Man') },
                      { value: 'V', label: t('form.genderOptions.female', 'Vrouw') },
                    ]}
                  />
                  <div style={{ ...styles.fieldContainer, ...styles.flex1 }}>
                    <DateOfBirthField
                      value={data?.Contractdocument?.PP?.BS?.BS_GEBDAT}
                      onChange={(value) => {
                        setData((prev: any) => setIn(prev, ['Contractdocument', 'PP', 'BS', 'BS_GEBDAT'], value));
                      }}
                      outputFormat="number"
                      required={true}
                      id="bs-date-of-birth"
                      style={{ ...styles.fieldInput, ...(validationErrors['BS_GEBDAT'] ? styles.fieldInputError : {}) }}
                      labelStyle={styles.fieldLabel}
                    />
                    {validationErrors['BS_GEBDAT'] && <p style={styles.errorMessage}>{t(validationErrors['BS_GEBDAT'])}</p>}
                  </div>
                </div>

              </>
            )}
          </div>

          {/* Vehicle Section */}
          <div style={styles.addressContainer}>
            <SectionHeader icon={Car} title={t('screens.personenauto.sections.vehicle')} />

            {/* Kenteken + Vehicle registered to - side by side (stacked on narrow screens) */}
            <div className="personenauto-field-pair personenauto-kenteken-pair" style={styles.rowGap16Start}>
              <div style={{ ...styles.fieldContainer, ...styles.flex1 }}>
                <label className="field-label--required kenteken-label" style={styles.fieldLabel} htmlFor="kenteken">
                  {t('screens.personenauto.fields.licensePlate')}
                </label>
                <div className="kenteken-row">
                <div style={{ ...styles.licensePlate, ...(validationErrors['OB_KENTEKE'] ? styles.licensePlateError : {}) }}>
                  <div style={styles.licensePlateBand}>
                    <svg style={styles.licensePlateEuEmblem} viewBox="0 0 20 20">
                      {[...Array(12)].map((_, i) => {
                        const angle = (i * 30 - 90) * (Math.PI / 180);
                        const cx = 10 + 7.5 * Math.cos(angle);
                        const cy = 10 + 7.5 * Math.sin(angle);
                        return <polygon key={i} points={`${cx},${cy-2} ${cx+0.7},${cy-0.6} ${cx+2},${cy-0.6} ${cx+1},${cy+0.4} ${cx+1.3},${cy+1.8} ${cx},${cy+1} ${cx-1.3},${cy+1.8} ${cx-1},${cy+0.4} ${cx-2},${cy-0.6} ${cx-0.7},${cy-0.6}`} fill="#ffcc00" />;
                      })}
                    </svg>
                    <span style={styles.licensePlateCountry}>NL</span>
                  </div>
                  <input
                    id="kenteken"
                    type="text"
                    value={(() => {
                      const stored = data?.Contractdocument?.PP?.OB?.OB_KENTEKE || '';
                      if (stored.length === 6) return formatLicensePlate(stored);
                      return kentekenInputValue;
                    })()}
                    onChange={(e) => {
                      const val = e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, '');
                      const cleaned = val.replace(/-/g, '').slice(0, 6);
                      setKentekenInputValue(cleaned.length < 6 ? val : '');
                      setData((prev: any) => ({
                        ...prev,
                        Contractdocument: { ...prev.Contractdocument, PP: { ...prev.Contractdocument?.PP, OB: { ...prev.Contractdocument?.PP?.OB, OB_KENTEKE: cleaned } } },
                      }));
                    }}
                    style={styles.licensePlateInput}
                    placeholder="AB-123-C"
                    maxLength={8}
                  />
                </div>
                {isLookingUpKenteken && (
                  <div className="kenteken-status">
                    <div className="price-spinner" />
                  </div>
                )}
                {kentekenLookupError && !isLookingUpKenteken && (
                  <span className="kenteken-status" style={styles.label15Error}>{kentekenLookupError}</span>
                )}
                </div>
                {validationErrors['OB_KENTEKE'] && <p style={styles.errorMessage}>{t(validationErrors['OB_KENTEKE'])}</p>}
              </div>

              {/* Vehicle registered to */}
              <SelectField
                label={t('screens.personenauto.fields.vehicleRegisteredTo', 'Voertuig staat op naam van')}
                required
                hidePlaceholder
                containerStyle={styles.flex1}
                value={data?.Supportsection?.Divers?.KentekenbewijshouderType || 'VP'}
                onChange={(val) => {
                    setData((prev: any) => {
                      const updated = {
                        ...prev,
                        Supportsection: {
                          ...prev.Supportsection,
                          Divers: { ...prev.Supportsection?.Divers, KentekenbewijshouderType: val },
                        },
                      };
                      const vp = prev.Contractdocument?.PP?.VP || {};
                      const bs = prev.Contractdocument?.PP?.BS || {};
                      const ci = relationData?.clientInfo;
                      let liData: any = {};
                      if (val === 'VP') {
                        liData = {
                          LI_RELSRTC: 'A', LI_ANAAM: ci?.lastName || '', LI_VOORL: ci?.initials || '',
                          LI_GESLACH: ci?.gender || 'O', LI_GEBDAT: Number(bs.BS_GEBDAT || 0),
                          LI_HUISNR: parseInt(String(vp.VP_HUISNR || 0), 10),
                          LI_PCODE: vp.VP_PCODE || '', LI_PLAATS: vp.VP_PLAATS || '', LI_STRAAT: vp.VP_STRAAT || '',
                        };
                      } else if (val === 'BS') {
                        liData = {
                          LI_RELSRTC: 'R', LI_ANAAM: bs.BS_ANAAM || '', LI_VOORL: bs.BS_VOORL || '',
                          LI_VOORV: bs.BS_VOORV || '', LI_GESLACH: bs.BS_GESLACH || 'O',
                          LI_GEBDAT: Number(bs.BS_GEBDAT || 0),
                          LI_HUISNR: parseInt(String(bs.BS_HUISNR || vp.VP_HUISNR || 0), 10),
                          LI_PCODE: bs.BS_PCODE || vp.VP_PCODE || '', LI_PLAATS: bs.BS_PLAATS || vp.VP_PLAATS || '',
                          LI_STRAAT: bs.BS_STRAAT || vp.VP_STRAAT || '',
                        };
                      }
                      updated.Contractdocument = {
                        ...prev.Contractdocument,
                        PP: { ...prev.Contractdocument?.PP, LI: liData },
                      };
                      return updated;
                    });
                }}
                options={[
                  { value: 'VP', label: t('screens.personenauto.fields.holderPolicyholder', 'Verzekeringnemer') },
                  { value: 'BS', label: t('screens.personenauto.fields.holderDriver', 'Regelmatig bestuurder') },
                ]}
              />
            </div>

            {kentekenDataFilled && !isLookingUpKenteken && !kentekenLookupError && (() => {
              const ob = data?.Contractdocument?.PP?.OB;
              const parts = [ob?.OB_MERK, ob?.OB_TYPE].filter(Boolean).join(' ');
              const year = ob?.OB_BOUWJR;
              const label = parts + (year ? ` ${t('screens.personenauto.messages.vehicleFromYear')} ${year}` : '');
              return (
                <span style={styles.coverageGroupLabel}>
                  {t('screens.personenauto.messages.vehicleFound')} | {label}
                </span>
              );
            })()}

            {/* Vehicle details - compact summary from kenteken lookup */}
            <VehicleSummaryCard ob={data?.Contractdocument?.PP?.OB} />

            {/* Localhost-only: editable vehicle fields for debugging Allianz acceptance declines (e.g. UWM12). */}
            {isDebugMode() && data?.Contractdocument?.PP?.OB && (data.Contractdocument.PP.OB.OB_MERK || data.Contractdocument.PP.OB.OB_MODEL) && (
              <div style={styles.vehicleSummaryCard}>
                <span style={styles.vehicleSummaryBadge}>Debug: voertuigvelden (alleen localhost)</span>
                <div style={styles.fieldRow}>
                  {Array.from(new Set([...Object.keys(data.Contractdocument.PP.OB), 'OB_TNOKLAS'])).map((key) => {
                    const current = data.Contractdocument.PP.OB[key];
                    const isNumber = typeof current === 'number';
                    return (
                      <div key={key} style={{ ...styles.fieldContainer, ...styles.fieldColumn }}>
                        <label style={styles.fieldLabel}>{key}</label>
                        <input
                          type={isNumber ? 'number' : 'text'}
                          value={current ?? ''}
                          onChange={(e) => handleDebugObChange(key, e.target.value, isNumber)}
                          style={styles.fieldInput}
                        />
                      </div>
                    );
                  })}
                </div>
                <button type="button" style={styles.button} onClick={handleManualCalculate} disabled={isCalculating}>
                  {isCalculating ? t('common.calculating', 'Berekenen…') : 'Herbereken'}
                </button>
                <div style={styles.divider10} />
                {AUTO_VARIANTS.map((v) => {
                  const xg = premiumsByVariant?.[v]?.Contractdocument?.XG;
                  const status = xg?.XG_STATUS
                    ? `${xg.XG_STATUS} ${xg.XG_STATUST || ''} ${xg.XM?.XM_STATTXT || ''}`.trim()
                    : 'geen resultaat';
                  return (
                    <p key={v} style={styles.hint13}>{v}: {status}</p>
                  );
                })}
              </div>
            )}

            {/* Vehicle power vs driver age warning */}
            {kwWarning && (
              <div id="kw-warning" style={styles.kwWarning}>
                <svg style={styles.errorIcon20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                <span style={styles.warningText}>{kwWarning}</span>
              </div>
            )}

            {/* Eerste eigenaar — drives which value-guarantee toggle appears in Regelingen:
                J → Nieuwwaarderegeling (CG 2040), N → Aanschafwaarderegeling (CG 2041). Only
                shown once we have vehicle data from the kenteken lookup; before that the
                question is meaningless. Switching the answer wipes any previously selected
                NW/AKW toggle so the two never end up both stored as 'J'. */}
            {(data?.Contractdocument?.PP?.OB?.OB_MERK || data?.Contractdocument?.PP?.OB?.OB_MODEL) && (
              <div style={styles.fieldRow}>
                <div id="ob-eerstei" style={{ ...styles.fieldContainer, ...styles.fieldColumn }}>
                  <label className="field-label--required" style={styles.fieldLabel}>
                    {t('screens.personenauto.fields.firstOwner', 'Huidige eigenaar is tevens eerste eigenaar')}
                  </label>
                  <div style={styles.radioGroup}>
                    {(['J', 'N'] as const).map((optValue) => {
                      const selected = (data?.Contractdocument?.PP?.OB?.OB_EERSTEI || '') === optValue;
                      return (
                        <div
                          key={optValue}
                          style={selected ? styles.radioOptionSelected : styles.radioOption}
                          onClick={() => {
                            setData((prev: any) => {
                              const updated = {
                                ...prev,
                                Contractdocument: {
                                  ...prev.Contractdocument,
                                  PP: {
                                    ...prev.Contractdocument?.PP,
                                    OB: { ...prev.Contractdocument?.PP?.OB, OB_EERSTEI: optValue },
                                  },
                                },
                                Supportsection: {
                                  ...prev.Supportsection,
                                  Dekkingen: {
                                    ...prev.Supportsection?.Dekkingen,
                                    Nieuwwaarderegeling: 'N',
                                    Aanschafwaarderegeling: 'N',
                                  },
                                },
                              };
                              setTimeout(() => saveStepData(SCREEN_ID, updated), 0);
                              return updated;
                            });
                          }}
                        >
                          <div style={selected ? styles.radioDotSelected : styles.radioDot}>
                            {selected && <div style={styles.radioDotInner} />}
                          </div>
                          {optValue === 'J' ? t('common.yes') : t('common.no')}
                        </div>
                      );
                    })}
                  </div>
                  {validationErrors['OB_EERSTEI'] && <p style={styles.errorMessage}>{t(validationErrors['OB_EERSTEI'])}</p>}
                </div>
                <SelectField
                  id="ob-tnoklas"
                  label={t('screens.personenauto.fields.securityClass')}
                  required
                  containerStyle={styles.fieldColumn}
                  value={data?.Contractdocument?.PP?.OB?.OB_TNOKLAS || ''}
                  onChange={(val) => {
                    setData((prev: any) => {
                      const updated = setIn(prev, ['Contractdocument', 'PP', 'OB', 'OB_TNOKLAS'], val);
                      setTimeout(() => saveStepData(SCREEN_ID, updated), 0);
                      return updated;
                    });
                  }}
                  placeholder={t('screens.personenauto.placeholders.select')}
                  error={validationErrors['OB_TNOKLAS'] ? t(validationErrors['OB_TNOKLAS']) : null}
                  options={(() => {
                    // Map the kenteken-endpoint value too: if it falls outside the shortlist,
                    // surface it as its own option so the pre-filled class stays visible/selectable.
                    const current = data?.Contractdocument?.PP?.OB?.OB_TNOKLAS;
                    const codes = current && !(SECURITY_CLASS_CODES as readonly string[]).includes(current)
                      ? [current, ...SECURITY_CLASS_CODES]
                      : SECURITY_CLASS_CODES;
                    return codes.map((code) => ({ value: code, label: t(`screens.personenauto.securityClassOptions.${code}`, { defaultValue: code }) }));
                  })()}
                />
              </div>
            )}

          </div>

          {/* Coverage Section */}
          <div style={styles.addressContainer}>
            <div style={styles.sectionHeader} className="personenauto-coverage-header">
              <div style={styles.rowCenterFlex}>
                <div style={{ ...styles.sectionIconContainer, ...styles.mr12Shrink }}>
                  <ShieldCheck size={28} weight="duotone" color="var(--secondary-color, #F51C17)" style={styles.iconOpaque} />
                </div>
                <h2 style={{ ...styles.addressTitle, ...styles.ellipsis }}>{t('screens.personenauto.sections.coverage')}</h2>
              </div>
              {(() => {
                if (isCalculating) {
                  return <span style={styles.premiumBadge}><span className="price-spinner" /></span>;
                }
                const dekkingen = data?.Supportsection?.Dekkingen || {};
                const hoofddekking = dekkingen.Hoofddekking || 'WA';
                const pp = data?.Contractdocument?.PP;
                const variantPp = premiumsByVariant[hoofddekking]?.Contractdocument?.PP;
                let total = sectionDisplayPrice(pp?.WA?.WA_BTP) + sectionDisplayPrice(pp?.CA?.[0]?.CA_BTP);
                if (dekkingen.Rechtsbijstand === 'J') total += sectionDisplayPrice(pp?.RP?.RP_BTP ?? variantPp?.RP?.RP_BTP);
                if (dekkingen.Schade_Inzittenden === 'J') total += sectionDisplayPrice(pp?.SV?.SV_BTP ?? variantPp?.SV?.SV_BTP);
                const cgBtp = (code: string) => {
                  const find = (arr: any) => (Array.isArray(arr) ? arr : [])
                    .find((e: any) => String(e?.CG_CODE) === code);
                  return find(pp?.CG)?.CG_BTP ?? find(variantPp?.CG)?.CG_BTP;
                };
                if (dekkingen.Vrije_keuze_herstelbedrijf === 'J' && hoofddekking !== 'WA') {
                  total += sectionDisplayPrice(cgBtp('2051'));
                }
                if (dekkingen.Nieuwwaarderegeling === 'J' && hoofddekking !== 'WA') {
                  total += sectionDisplayPrice(cgBtp('2040'));
                }
                if (dekkingen.Aanschafwaarderegeling === 'J' && hoofddekking !== 'WA') {
                  total += sectionDisplayPrice(cgBtp('2041'));
                }
                if (dekkingen.Pechhulp === 'J') total += sectionDisplayPrice(pp?.PH?.PH_BTP ?? variantPp?.PH?.PH_BTP);
                if (total > 0) {
                  return (
                    <span style={styles.premiumBadge}>
                      {t('screens.personenauto.sections.total')}: &euro;{formatPrice(total)}
                    </span>
                  );
                }
                return <span style={styles.premiumBadge}><span>&euro; —</span></span>;
              })()}
            </div>

            {/* Hoofddekking - 3 options as cards */}
            <div style={styles.fieldContainer}>
              <div style={styles.rowCenterWrap}>
                <label className="field-label--required" style={{ ...styles.fieldLabel, ...styles.mb0 }}>
                  {t('screens.personenauto.fields.mainCoverage')}
                </label>
                <button
                  type="button"
                  onClick={() => setShowCoverageMatrix((s) => !s)}
                  style={styles.coverageMatrixToggle}
                >
                  {t(`screens.personenauto.coverageMatrix.${showCoverageMatrix ? 'hide' : 'show'}`)}
                </button>
              </div>
              <div className="personenauto-coverage-options">
                {([
                  { value: 'WA', label: t('screens.personenauto.coverage.wa'), description: t('screens.personenauto.coverage.waDesc') },
                  { value: 'WA+', label: t('screens.personenauto.coverage.waPlus'), description: t('screens.personenauto.coverage.waPlusDesc') },
                  { value: 'WA+volledig', label: t('screens.personenauto.coverage.waFull'), description: t('screens.personenauto.coverage.waFullDesc') },
                ] as const).map((option) => {
                  const selected = (data?.Supportsection?.Dekkingen?.Hoofddekking || 'WA') === option.value;

                  // For the SELECTED variant, read from workflow data so the card matches
                  // the summary page (which also reads workflow). The package endpoint
                  // recomputes CA_BTP slightly after the parallel BFF calls, so the cached
                  // pre-package values can drift 1¢ from the post-package workflow state.
                  // Unselected variants still use the cache because workflow only holds
                  // the selected variant's data.
                  const source = selected
                    ? data?.Contractdocument?.PP
                    : premiumsByVariant[option.value]?.Contractdocument?.PP;
                  const basePremium = sectionDisplayPrice(Number(source?.WA?.WA_BTP || 0))
                    + sectionDisplayPrice(Number(source?.CA?.[0]?.CA_BTP || 0));

                  const optionRefused = option.value !== 'WA' && isVariantRefused(option.value) && !isVariantRefused('WA');

                  return (
                    <CoverageOptionCard
                      key={option.value}
                      selected={selected}
                      label={option.label}
                      description={option.description}
                      price={basePremium > 0 ? `€${formatPrice(basePremium)}` : null}
                      disabled={optionRefused}
                      disabledReason={t('screens.personenauto.coverage.cascoRefusedCard')}
                      onSelect={() => {
                        // Swap to the pre-calculated variant (no BFF call needed)
                        const cachedResult = premiumsByVariant[option.value];
                        setData((prev: any) => {
                          const updated = {
                            ...prev,
                            Supportsection: {
                              ...prev.Supportsection,
                              Dekkingen: {
                                ...prev.Supportsection?.Dekkingen,
                                Hoofddekking: option.value,
                                // VKH disappears on WA-only — reset the toggle so its 'J'
                                // doesn't carry over the next time the advisor picks Casco.
                                ...(option.value === 'WA' ? { Vrije_keuze_herstelbedrijf: 'N' } : {}),
                                // NW/AKW only price on Volledig — reset them whenever the
                                // advisor isn't on Volledig so the rows + payload stay clean.
                                ...(option.value !== 'WA+volledig' ? { Nieuwwaarderegeling: 'N', Aanschafwaarderegeling: 'N' } : {}),
                              },
                            },
                          };

                          // If we have cached BFF results for this variant, merge them in
                          if (cachedResult) {
                            const merged = mergeResultIntoData(cachedResult, updated);
                            setTimeout(() => saveStepData(SCREEN_ID, merged), 0);
                            return merged;
                          }

                          // No cached result yet - set CA locally and let auto-calc pick it up
                          updated.Contractdocument = {
                            ...prev.Contractdocument,
                            PP: {
                              ...prev.Contractdocument?.PP,
                              WA: {
                                ...prev.Contractdocument?.PP?.WA,
                                WA_CODE: '2001',
                                WA_PPRC: prev.Contractdocument?.PP?.WA?.WA_PPRC ?? 20,
                              },
                            },
                          };

                          if (option.value === 'WA') {
                            delete updated.Contractdocument.PP.CA;
                          } else if (option.value === 'WA+') {
                            updated.Contractdocument.PP.CA = [{
                              CA_CODE: '2003', CA_ENTREF: '2003', CA_ERB: 0,
                              CA_PPRC: prev.Contractdocument?.PP?.CA?.[0]?.CA_PPRC ?? 20,
                            }];
                          } else if (option.value === 'WA+volledig') {
                            updated.Contractdocument.PP.CA = [{
                              CA_CODE: '2002', CA_ENTREF: '2002', CA_ERB: 0,
                              CA_PPRC: prev.Contractdocument?.PP?.CA?.[0]?.CA_PPRC ?? 20,
                            }];
                          }

                          return updated;
                        });
                      }}
                    />
                  );
                })}
              </div>
              {cascoRefused && (
                <div style={{ ...styles.kwWarning, ...styles.coverageNoticeMt }}>
                  <svg style={styles.errorIcon20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                  <span style={styles.warningText}>{t('screens.personenauto.coverage.cascoRefusedNotice')}</span>
                </div>
              )}
              <CoverageHighlights selectedHoofd={(data?.Supportsection?.Dekkingen?.Hoofddekking || 'WA') as 'WA' | 'WA+' | 'WA+volledig'} />
              <CoverageMatrix selectedHoofd={(data?.Supportsection?.Dekkingen?.Hoofddekking || 'WA') as 'WA' | 'WA+' | 'WA+volledig'} open={showCoverageMatrix} />
            </div>

            {/* Alarm requirement notice - disabled until beveiligingsklasse logic is implemented */}

            {/* Eigen risico + Aankoopdatum - only for WA+volledig */}
            {(data?.Supportsection?.Dekkingen?.Hoofddekking || 'WA') === 'WA+volledig' && (
              <>
                <div style={styles.rowGap16Top8}>
                  <SelectField
                    id="ca-erb"
                    label={t('screens.personenauto.fields.deductible', 'Eigen risico')}
                    hidePlaceholder
                    containerStyle={styles.flex1}
                    value={String(data?.Contractdocument?.PP?.CA?.[0]?.CA_ERB ?? 0)}
                    onChange={(v) => {
                      const val = parseInt(v, 10);
                      setData((prev: any) => {
                        const ca = prev.Contractdocument?.PP?.CA?.[0] || {};
                        return {
                          ...prev,
                          Contractdocument: {
                            ...prev.Contractdocument,
                            PP: {
                              ...prev.Contractdocument?.PP,
                              CA: [{ ...ca, CA_ERB: val }],
                            },
                          },
                        };
                      });
                      // Only recalculate WA+volledig (delay to let state flush)
                      setTimeout(() => recalculateSingleVariant('WA+volledig'), 300);
                    }}
                    options={[
                      { value: '0', label: '€ 0' },
                      { value: '250', label: '€ 250' },
                      { value: '500', label: '€ 500' },
                      { value: '1000', label: '€ 1.000' },
                    ]}
                  />
                </div>
              </>
            )}

            {(() => {
              const ob = data?.Contractdocument?.PP?.OB;
              const hoofd = data?.Supportsection?.Dekkingen?.Hoofddekking || 'WA';
              const bj = parseInt(String(ob?.OB_BOUWJR || '0'), 10);
              const bm = parseInt(String(ob?.OB_BOUWMND || '1'), 10);
              const _now = new Date();
              const ageMo = bj > 0 ? (_now.getFullYear() - bj) * 12 + (_now.getMonth() + 1 - bm) : 0;
              const vwaca = Number(ob?.OB_VWACA || 0);
              return hoofd === 'WA+volledig' && ob?.OB_EERSTEI === 'N'
                && bj > 0 && ageMo > 12 && ageMo <= 96 && vwaca <= 100000;
            })() && (
              <div style={styles.rowGap16Top8}>
                <div style={{ ...styles.fieldContainer, ...styles.flex1 }}>
                  <label className="field-label--required" style={styles.fieldLabel} htmlFor="aankdat">
                    {t('screens.personenauto.fields.purchaseDate', 'Aankoopdatum')}
                  </label>
                  <DatePicker
                    id="aankdat"
                    value={(() => {
                      const val = data?.Contractdocument?.PP?.OB?.OB_AANKDAT;
                      if (!val) return '';
                      const s = String(val);
                      if (s.length === 8 && !s.includes('-')) {
                        return `${s.slice(6, 8)}-${s.slice(4, 6)}-${s.slice(0, 4)}`;
                      }
                      return s;
                    })()}
                    onChange={(dateStr: string) => {
                      if (dateStr && dateStr.length === 10) {
                        // Store OB_AANKDAT as a YYYYMMDD string (matching OB_DATTOEL). Sending
                        // it as a number silently breaks Allianz's AKW window check (UWM14).
                        const parts = dateStr.split('-');
                        const strVal = `${parts[2]}${parts[1]}${parts[0]}`;
                        setData((prev: any) => ({
                          ...prev,
                          Contractdocument: { ...prev.Contractdocument, PP: { ...prev.Contractdocument?.PP, OB: { ...prev.Contractdocument?.PP?.OB, OB_AANKDAT: strVal } } },
                        }));
                      } else if (!dateStr) {
                        setData((prev: any) => ({
                          ...prev,
                          Contractdocument: { ...prev.Contractdocument, PP: { ...prev.Contractdocument?.PP, OB: { ...prev.Contractdocument?.PP?.OB, OB_AANKDAT: undefined } } },
                        }));
                      }
                    }}
                    pastOnly={true}
                    minDate={(() => {
                      // Earliest allowed = max(OB_DATTOEL, today − 72 months). A purchase
                      // date before the vehicle's first registration is nonsense and earns
                      // a UWM14 from Allianz, so we keep the calendar from offering it.
                      const windowFloor = new Date(); windowFloor.setMonth(windowFloor.getMonth() - 72);
                      const dattoelRaw = String(data?.Contractdocument?.PP?.OB?.OB_DATTOEL || '');
                      if (dattoelRaw.length === 8) {
                        const dy = parseInt(dattoelRaw.substring(0, 4), 10);
                        const dm = parseInt(dattoelRaw.substring(4, 6), 10);
                        const dd = parseInt(dattoelRaw.substring(6, 8), 10);
                        if (dy && dm && dd) {
                          const dattoel = new Date(dy, dm - 1, dd);
                          if (dattoel.getTime() > windowFloor.getTime()) return dattoel;
                        }
                      }
                      return windowFloor;
                    })()}
                    maxDate={new Date()}
                    placeholder="DD-MM-JJJJ"
                    style={{ ...styles.fieldInput, ...(validationErrors['OB_AANKDAT'] ? styles.fieldInputError : {}) }}
                  />
                  {validationErrors['OB_AANKDAT'] && <p style={styles.errorMessage}>{t(validationErrors['OB_AANKDAT'])}</p>}
                </div>
                <div style={styles.flex1} />
              </div>
            )}

            <hr style={styles.sectionDivider} />

            {/* Optional coverages - toggle switches with premiums */}
            <div className="personenauto-optional-coverages">
              {AUTO_OPTIONAL_COVERAGES.map((coverage) => {
                const hoofddekking = data?.Supportsection?.Dekkingen?.Hoofddekking || 'WA';
                const ob = data?.Contractdocument?.PP?.OB;
                const bj = parseInt(String(ob?.OB_BOUWJR || '0'), 10);
                const bm = parseInt(String(ob?.OB_BOUWMND || '1'), 10);
                const now = new Date();
                const vehicleAgeMonths = bj > 0 ? (now.getFullYear() - bj) * 12 + (now.getMonth() + 1 - bm) : 0;
                const hasCasco = hasCascoSelected(data?.Supportsection?.Dekkingen);
                const hasFullCasco = hoofddekking === 'WA+volledig';

                // VKH is casco-only: on a WA policy it is hidden (via the isDisabled
                // early-return below) rather than shown as a greyed, non-selectable row.
                let cascoToggleBlocked = false;
                if (coverage.key === 'Vrije_keuze_herstelbedrijf') {
                  cascoToggleBlocked = !hasCasco;
                }

                // NW and AKW HIDE entirely when their gate isn't met — Allianz only prices them
                // on Volledig Casco (Beperkt returns CG_BTP=0), so showing a disabled row there
                // would be misleading. OB_EERSTEI picks exactly one of the two, so at most one
                // row is ever rendered. ≤ boundaries are inclusive (a car built exactly 36
                // months ago still qualifies for NW). bj===0 → no kenteken data yet, both stay
                // hidden.
                //
                // AKW (mirrors autoGarantie.ts): vehicle within 8 years of build (≤ 96 mo, NO
                // lower age floor), OB_VWACA ≤ €100.000 incl. btw, purchased within 1 year (the
                // OB_AANKDAT window is enforced at calc/eligibility time). NW (first-owner) caps at
                // ≤ 36 months. ≤ boundaries inclusive; bj===0 → no kenteken yet, both stay hidden.
                if (coverage.key === 'Nieuwwaarderegeling') {
                  const isFirstOwner = ob?.OB_EERSTEI === 'J';
                  if (!hasFullCasco || !isFirstOwner || bj === 0 || vehicleAgeMonths > 36) return null;
                } else if (coverage.key === 'Aanschafwaarderegeling') {
                  const isNotFirstOwner = ob?.OB_EERSTEI === 'N';
                  const vwaca = Number(ob?.OB_VWACA || 0);
                  if (!hasFullCasco || !isNotFirstOwner || bj === 0
                    || vehicleAgeMonths > 96
                    || vwaca > 100000) return null;
                }

                // Pechhulp: vehicle must be younger than 12 years.
                let vehicleTooOld = false;
                if (coverage.requiresYoungVehicle) {
                  vehicleTooOld = bj === 0 || vehicleAgeMonths >= 12 * 12;
                }

                const isDisabled = coverage.disabled || vehicleTooOld || cascoToggleBlocked;
                // Hide extra coverages that can't be selected yet (casco-only VKH on a WA
                // policy, Pechhulp before a kenteken / on an older car) instead of showing a
                // faded, non-selectable row whose (i) info is too dimmed to read.
                if (isDisabled) return null;
                const isEnabled = !isDisabled && data?.Supportsection?.Dekkingen?.[coverage.key] === 'J';
                const premiumData = resolveCoverageEntry(data?.Contractdocument?.PP, coverage);
                const previewEntry = resolveCoverageEntry(
                  premiumsByVariant[hoofddekking]?.Contractdocument?.PP, coverage,
                );
                const premium = sectionDisplayPrice(
                  premiumData?.[coverage.premiumField] ?? previewEntry?.[coverage.premiumField],
                );
                // Split the label into atoms: words first, then each word further at common Dutch
                // compound boundaries, so a long label wraps at a natural point (e.g.
                // "Nieuwwaarde|regeling") rather than mid-syllable. `space` marks atoms that follow
                // a real word boundary (rendered as a space) vs a compound boundary (a <wbr>). The
                // (i) icon is glued to the LAST atom (no-wrap) so it stays inline with the text on
                // the left and never wraps onto its own line.
                const labelAtoms: { text: string; space: boolean }[] = [];
                t(coverage.labelKey).trim().split(/\s+/).forEach((word, wi) => {
                  word.split(/(?=waarde|regeling|garantie|bedrijf|herstel|bijstand|hulp)/i)
                    .filter(Boolean)
                    .forEach((part, pi) => labelAtoms.push({ text: part, space: wi > 0 && pi === 0 }));
                });
                const infoItems = [1, 2, 3, 4, 5]
                  .map((idx) => {
                    const itemKey = `screens.personenauto.coverageInfo.${coverage.infoKey}.item${idx}`;
                    const interpolations = coverage.infoKey === 'passengerDamage'
                      ? { defaultAmount: formatPrice(passengerInsuredSumDefault) }
                      : undefined;
                    return t(itemKey, interpolations);
                  })
                  .filter((text, idx) => text !== `screens.personenauto.coverageInfo.${coverage.infoKey}.item${idx + 1}`)
                  .map((text) => ({ text }));
                return (
                  <React.Fragment key={coverage.key}>
                  <div style={styles.toggleRow}>
                    <div style={styles.rowBetweenFull}>
                      <div style={styles.toggleLabelCol}>
                        <label style={isDisabled ? { ...styles.toggleLabel, opacity: 0.5 } : styles.toggleLabel}>
                          {labelAtoms.map((atom, i) => {
                            const sep = i === 0 ? null : (atom.space ? ' ' : <wbr />);
                            if (i < labelAtoms.length - 1) return <React.Fragment key={i}>{sep}{atom.text}</React.Fragment>;
                            return (
                              <React.Fragment key={i}>
                                {sep}
                                <span style={styles.labelTailNoWrap}>
                                  {atom.text}
                                  <InfoTooltip
                                    title={t(`screens.personenauto.coverageInfo.${coverage.infoKey}.title`)}
                                    items={infoItems}
                                    popupClassName="info-tooltip-popup-card"
                                    wrapperStyle={styles.infoButtonWrapper}
                                  />
                                </span>
                              </React.Fragment>
                            );
                          })}
                        </label>
                      </div>
                      <div style={styles.rowCenter12}>
                        {premium > 0 && !isDisabled && (
                          <span style={{
                            ...styles.coveragePremiumBadge,
                            ...(isEnabled ? {} : styles.badgeDisabled),
                          }}>
                            &euro;{formatPrice(premium)}
                          </span>
                        )}
                        <div
                          onClick={() => {
                            if (isDisabled) return;
                            setData((prev: any) => {
                              const updated = {
                                ...prev,
                                Supportsection: {
                                  ...prev.Supportsection,
                                  Dekkingen: {
                                    ...prev.Supportsection?.Dekkingen,
                                    [coverage.key]: isEnabled ? 'N' : 'J',
                                  },
                                },
                              };
                              setTimeout(() => saveStepData(SCREEN_ID, updated), 0);
                              return updated;
                            });
                          }}
                          style={{
                            ...styles.toggleSwitch,
                            backgroundColor: isEnabled ? 'var(--primary-color, #056666)' : 'var(--surface-muted, #f7f9fc)',
                            ...(isDisabled ? styles.toggleDisabled : {}),
                          }}
                        >
                          <div style={{
                            ...styles.toggleSlider,
                            transform: isEnabled ? 'translateY(-50%) translateX(24px)' : 'translateY(-50%) translateX(2px)',
                          }} />
                        </div>
                      </div>
                    </div>
                  </div>
                  </React.Fragment>
                );
              })}
            </div>
          </div>

            </div>

            {/* Validation summary banner - visible next to Next button */}
            <ValidationSummary validationErrors={validationErrors} />

            <WorkflowNavButtons
              onBack={handleBack}
              onNext={handleNext}
              nextLabel={isNextCalculating ? 'screens.personenauto.calculating' : 'common.nextPlain'}
              nextLoading={isNextCalculating}
              center={
                <div style={styles.productCounter}>
                  {t('common.productCounter', { current: productIndex, total: totalProducts })}
                </div>
              }
            />
          </WithHelpSidebar>

          {/* Debug panel (collapsible) */}
          <DebugPanel
            input={lastCalculationInput}
            output={lastCalculationOutput}
            onRecalculate={handleManualCalculate}
            calculating={isCalculating}
            error={calculationError}
            validationErrors={validationErrors}
          />

        </div>
      </main>
    </WorkflowPageLayout>
  );
};


export default PersonenAutoScreen;
